//
// timeline.ts — the data model for animated map renders, plus the
// pure functions that evaluate a timeline at any given point in time.
//
// Mental model:
//
//   An animation is an *initial view* (the map's camera at t=0) plus
//   a list of *actions*. Each action drives exactly one property
//   track (center / zoom / bearing / pitch) between a start and end
//   time. Actions on the same track must not overlap. Actions on
//   different tracks are free to overlap — that's how you get
//   "pan while rotating while zooming in".
//
//   At any time t, the value of a property is computed by walking
//   the actions on its track in time order:
//
//     - If t is inside an action's window, interpolate from the
//       baseline (the value the property held going into the action)
//       to the action's target, eased by the action's curve.
//     - If t is past the end of the most recent action, hold that
//       action's end value.
//     - If no action has reached t yet, hold the initial view's
//       value.
//
//   The baseline going into an action is the property's value at
//   the instant the action starts, computed by the same rules —
//   i.e. the end value of the previous action on the same track,
//   or the initial view's value if there is none.
//
// Fade in/out is a separate envelope applied as an alpha-black
// composite at render time; it does not interact with the map state.
//

//
// A camera state — the set of properties we animate. Matches what
// Mapbox's jumpTo takes, modulo the LngLat object shape.
//
export interface MapView {
  center: { lng: number; lat: number };
  zoom: number;
  bearing: number;
  pitch: number;
}

//
// Which property a track drives. Derivable from an action's type
// but exposed as a union for use in UI code (filtering by track,
// track-row rendering).
//
export type Track = 'center' | 'zoom' | 'bearing' | 'pitch';

//
// The four easing curves we support. Custom cubic-bezier support
// can come later if needed.
//
export type Easing = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';

//
// Fields every action shares.
//
// `smooth` opts the action into Catmull-Rom spline interpolation
// instead of pair-based linear interpolation. Consecutive smooth
// actions on the same track (with no gap between them) are grouped
// together and their targets become waypoints of a single C¹-
// continuous curve. A non-smooth action, a gap, or the end of the
// track terminates the group.
//
// Within a smooth group the action's `easing` is ignored — the
// spline carries the smoothness, and layering easing on top would
// break C¹ continuity at the waypoints (the thing we went to the
// trouble of building a spline for).
//
interface ActionBase {
  id: string;
  start: number;
  end: number;
  easing: Easing;
  smooth?: boolean;
}

//
// Pan action: animates the center on the center track.
//
export interface PanAction extends ActionBase {
  type: 'pan';
  target: { lng: number; lat: number };
}

//
// Zoom action: animates the zoom scalar on the zoom track.
//
export interface ZoomAction extends ActionBase {
  type: 'zoom';
  target: number;
}

//
// Pitch action: animates pitch on the pitch track. Mapbox clamps
// pitch to [0, 85]; we validate in the UI, not here.
//
export interface PitchAction extends ActionBase {
  type: 'pitch';
  target: number;
}

//
// Rotate action: animates bearing on the bearing track, in one of
// two modes:
//
//   - absolute: interpolate from the baseline to a specific
//     compass heading via the shortest arc.
//   - delta: rotate by a signed number of degrees from the
//     baseline. This is what you want for "spin 360° around the
//     current point", which the shortest-arc path would collapse
//     to a no-op.
//
export type RotateAction =
  | (ActionBase & { type: 'rotate'; mode: 'absolute'; target: number })
  | (ActionBase & { type: 'rotate'; mode: 'delta'; deltaDegrees: number });

export type Action = PanAction | ZoomAction | PitchAction | RotateAction;

//
// Return which track an action drives.
//
export function trackOf(action: Action): Track {
  switch (action.type) {
    case 'pan': return 'center';
    case 'zoom': return 'zoom';
    case 'pitch': return 'pitch';
    case 'rotate': return 'bearing';
  }
}

//
// The complete animation description. totalSeconds is the render
// duration; actions beyond it are clamped by the UI. fadeIn and
// fadeOut are optional envelopes in seconds (0 or undefined = none).
//
export interface Timeline {
  actions: Action[];
  totalSeconds: number;
  fadeIn?: number;
  fadeOut?: number;
}

//
// Configuration for which HTML elements overlaying the map should
// be captured into the rendered video.
//
// Mapbox renders markers, popups, and UI controls (zoom in/out,
// attribution, ...) as DOM nodes that sit *outside* the WebGL
// canvas. Our fast-path canvas capture misses them entirely.
//
// The rules:
//
//   - `include` is a list of CSS selectors. If non-empty, the
//     capture pipeline rasterises any matching elements (and their
//     subtrees, plus the ancestor chain needed to render them)
//     and composites the result over the WebGL canvas pixels.
//   - `exclude` is a list of CSS selectors that strip matching
//     elements (and their subtrees) even if they sit inside an
//     included element. E.g. include = ['.mapboxgl-marker'],
//     exclude = ['.farm-name'] keeps the marker body and drops
//     the label.
//   - If `include` is empty, overlay capture is skipped entirely —
//     the encoder runs the pure canvas fast-path, no html-to-image
//     cost. This is the default state of a fresh panel.
//
export interface OverlayConfig {
  include: string[];
  exclude: string[];
}

//
// Lookup table mapping each easing kind to its curve function.
// Keys are normalized time in [0, 1]; values likewise.
//
const EASING: Record<Easing, (t: number) => number> = {
  'linear': (t) => t,
  'ease-in': (t) => t * t * t,
  'ease-out': (t) => 1 - Math.pow(1 - t, 3),
  'ease-in-out': (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
};

//
// Evaluate the whole camera state at time t.
//
export function computeViewAtTime(
  timeline: Timeline,
  initialView: MapView,
  t: number,
): MapView {
  return {
    center: computeCenter(timeline.actions, initialView.center, t),
    zoom: computeScalar(timeline.actions, 'zoom', initialView.zoom, t),
    bearing: computeBearing(timeline.actions, initialView.bearing, t),
    pitch: computeScalar(timeline.actions, 'pitch', initialView.pitch, t),
  };
}

//
// Evaluate the fade-to-black alpha at time t. Returns a value in
// [0, 1]: 0 means the map is fully visible; 1 means the frame is
// fully black. The two envelopes (fadeIn, fadeOut) are combined with
// max() so you can't accidentally darken the middle of the video by
// setting both.
//
export function computeFadeAlpha(timeline: Timeline, t: number): number {
  let alpha = 0;
  const { fadeIn, fadeOut, totalSeconds } = timeline;
  if (fadeIn && fadeIn > 0 && t < fadeIn) {
    alpha = Math.max(alpha, 1 - t / fadeIn);
  }
  if (fadeOut && fadeOut > 0 && t > totalSeconds - fadeOut) {
    alpha = Math.max(alpha, (t - (totalSeconds - fadeOut)) / fadeOut);
  }
  return Math.max(0, Math.min(1, alpha));
}

//
// Per-layer opacity fade. Drives the opacity of a single Mapbox
// style layer between two times:
//
//   direction: 'in'  → 0 before startT, linear 0→1 across [startT,
//                      endT], 1 after endT.
//   direction: 'out' → 1 before startT, linear 1→0 across [startT,
//                      endT], 0 after endT.
//
// The resulting factor is MULTIPLIED with the layer's original
// paint opacity (raster-opacity, line-opacity, etc.). If a layer
// has multiple fades (e.g. a fade-in at 10s and a fade-out at 50s)
// their factors are multiplied together — so a layer is fully
// visible only when ALL of its fades resolve to 1.
//
export interface LayerFade {
  //
  // Stable identifier so React keys and CRUD operations don't
  // depend on layerId + direction + start + end uniqueness.
  //
  id: string;
  //
  // Mapbox style layer id this fade controls. Must match an id
  // present in the host map's style at render time; unknown ids
  // are silently skipped with a console warning.
  //
  layerId: string;
  direction: 'in' | 'out';
  startT: number;
  endT: number;
}

//
// Collapse a list of LayerFades into a { layerId → factor } map at
// time t. Factors are clamped to [0, 1]; multiple fades on the
// same layer multiply together.
//
export function computeLayerOpacities(
  fades: LayerFade[],
  t: number,
): Record<string, number> {
  const factors: Record<string, number> = {};
  for (const f of fades) {
    const dur = Math.max(0.0001, f.endT - f.startT);
    let local: number;
    if (t <= f.startT) local = 0;
    else if (t >= f.endT) local = 1;
    else local = (t - f.startT) / dur;
    const factor = f.direction === 'in' ? local : 1 - local;
    const clamped = Math.max(0, Math.min(1, factor));
    const prev = factors[f.layerId];
    factors[f.layerId] = prev === undefined ? clamped : prev * clamped;
  }
  return factors;
}

//
// Shared segment shape the generic helper operates on. Every
// action type satisfies this via ActionBase.
//
interface Seg {
  start: number;
  end: number;
  easing: Easing;
  smooth?: boolean;
}

//
// Generic track evaluator. Walks the track's sorted segments,
// advancing a baseline value across completed segments, and at
// time t either:
//
//   - returns baseline if t is past the end of all segments, or
//     lies in a gap before / between segments,
//   - runs a linear interpolation across the active non-smooth
//     segment, or
//   - identifies the smooth group containing the active segment
//     and runs a Catmull-Rom spline across its waypoints.
//
// `advance` is how a segment updates the baseline (center → its
// target; bearing → baseline + signed delta; etc.).
// `lerp` is the linear interpolator for non-smooth segments.
// `spline` is the Catmull-Rom interpolator used within smooth
// groups.
//
function computeTrackAt<S extends Seg, V>(
  segments: S[],
  initial: V,
  t: number,
  advance: (baseline: V, seg: S) => V,
  lerp: (from: V, to: V, t: number) => V,
  spline: (waypoints: V[], times: number[], t: number) => V,
): V {
  // Walk completed segments to bring baseline up to the start of
  // the segment containing t (or past t entirely).
  let baseline = initial;
  let i = 0;
  while (i < segments.length && t >= (segments[i] as S).end) {
    baseline = advance(baseline, segments[i] as S);
    i++;
  }
  if (i >= segments.length || t < (segments[i] as S).start) return baseline;

  const seg = segments[i] as S;

  if (seg.smooth) {
    // Widen the window to the full smooth group. Consecutive
    // smooth segments joined end-to-start (no gap) form one group.
    let groupStart = i;
    while (
      groupStart > 0 &&
      (segments[groupStart - 1] as S).smooth &&
      areTouching((segments[groupStart - 1] as S).end, (segments[groupStart] as S).start)
    ) {
      groupStart--;
    }
    let groupEnd = i;
    while (
      groupEnd + 1 < segments.length &&
      (segments[groupEnd + 1] as S).smooth &&
      areTouching((segments[groupEnd] as S).end, (segments[groupEnd + 1] as S).start)
    ) {
      groupEnd++;
    }

    // Rewind to obtain the baseline at the group's start. We can't
    // reuse the baseline above because it may already have been
    // advanced across smooth segments that belong to this group.
    let groupBaseline = initial;
    for (let j = 0; j < groupStart; j++) {
      groupBaseline = advance(groupBaseline, segments[j] as S);
    }

    // Build parallel arrays of waypoint values and the times at
    // which they occur. The first waypoint is the group baseline
    // at the group's start time; subsequent waypoints are each
    // segment's post-advance value at its own end time.
    const waypoints: V[] = [groupBaseline];
    const times: number[] = [(segments[groupStart] as S).start];
    let cur = groupBaseline;
    for (let j = groupStart; j <= groupEnd; j++) {
      const s = segments[j] as S;
      cur = advance(cur, s);
      waypoints.push(cur);
      times.push(s.end);
    }

    return spline(waypoints, times, t);
  }

  // Linear branch.
  const eased = evalEasing(seg.easing, (t - seg.start) / (seg.end - seg.start));
  const endValue = advance(baseline, seg);
  return lerp(baseline, endValue, eased);
}

//
// Center track: pan actions, 2D spline / lerp in lng-lat space.
//
function computeCenter(
  actions: Action[],
  initial: { lng: number; lat: number },
  t: number,
): { lng: number; lat: number } {
  const pans = actions.filter(isPan).sort(byStart);
  return computeTrackAt<PanAction, { lng: number; lat: number }>(
    pans,
    initial,
    t,
    (_b, seg) => seg.target,
    lerpLngLat,
    splineLngLat,
  );
}

//
// Zoom and pitch track: scalar target, scalar spline / lerp.
//
function computeScalar(
  actions: Action[],
  kind: 'zoom' | 'pitch',
  initial: number,
  t: number,
): number {
  const scalars = actions
    .filter((a): a is ZoomAction | PitchAction => a.type === kind)
    .sort(byStart);
  return computeTrackAt<ZoomAction | PitchAction, number>(
    scalars,
    initial,
    t,
    (_b, seg) => seg.target,
    lerpScalar,
    splineScalar,
  );
}

//
// Bearing track: rotate actions, 1D scalar spline on an UNWRAPPED
// cumulative heading so angle wrap-around at ±180° doesn't break
// the interpolator. Absolute rotations contribute their shortest-
// arc signed delta; delta rotations contribute their signed amount
// as specified.
//
function computeBearing(actions: Action[], initial: number, t: number): number {
  const rotates = actions.filter(isRotate).sort(byStart);
  return computeTrackAt<RotateAction, number>(
    rotates,
    initial,
    t,
    (baseline, seg) => baseline + rotateDelta(baseline, seg),
    lerpScalar,
    splineScalar,
  );
}

//
// Signed angular delta contributed by a rotate action, in degrees.
// For absolute mode, takes the shortest arc from the current
// baseline to the target. For delta mode, uses the specified
// (directed) amount so multi-turn spins and "go around the long
// way" intents are preserved.
//
function rotateDelta(baseline: number, action: RotateAction): number {
  if (action.mode === 'delta') return action.deltaDegrees;
  return ((action.target - baseline + 540) % 360) - 180;
}

//
// Two floats are "touching" for smooth-group purposes if they're
// within a tenth of a frame at 60 fps — far below user perception
// but tolerant of drag-and-drop imprecision in the timeline UI.
//
function areTouching(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-3;
}

//
// Linear interpolation primitives used by the generic helper.
//
function lerpLngLat(
  from: { lng: number; lat: number },
  to: { lng: number; lat: number },
  t: number,
): { lng: number; lat: number } {
  return {
    lng: from.lng + (to.lng - from.lng) * t,
    lat: from.lat + (to.lat - from.lat) * t,
  };
}

function lerpScalar(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

//
// Catmull-Rom spline interpolation in 1D. Given a sorted list of
// waypoints and the times at which they occur, returns the
// interpolated value at time t. The spline has C¹ continuity at
// each interior waypoint; endpoints are handled by reflecting the
// first and last segments to produce phantom neighbours, which
// makes the tangent at each end tangential to the adjacent segment
// rather than abruptly flat.
//
// Time parameterisation is uniform within each segment: within
// waypoint i → i+1, the local parameter u = (t − times[i]) /
// (times[i+1] − times[i]). This means different segment durations
// produce different velocity magnitudes at the waypoint boundaries,
// but direction is continuous — so paths don't kink, they just
// speed up or slow down. Good enough for most use.
//
function splineScalar(waypoints: number[], times: number[], t: number): number {
  const i = findSegmentIndex(times, t);
  const tA = times[i] as number;
  const tB = times[i + 1] as number;
  const u = (t - tA) / (tB - tA);
  const p1 = waypoints[i] as number;
  const p2 = waypoints[i + 1] as number;
  const p0 = i === 0 ? 2 * p1 - p2 : (waypoints[i - 1] as number);
  const p3 = i + 2 < waypoints.length ? (waypoints[i + 2] as number) : 2 * p2 - p1;
  return catmullRom1D(p0, p1, p2, p3, u);
}

//
// Catmull-Rom in 2D — component-wise scalar spline.
//
function splineLngLat(
  waypoints: Array<{ lng: number; lat: number }>,
  times: number[],
  t: number,
): { lng: number; lat: number } {
  const i = findSegmentIndex(times, t);
  const tA = times[i] as number;
  const tB = times[i + 1] as number;
  const u = (t - tA) / (tB - tA);
  const p1 = waypoints[i] as { lng: number; lat: number };
  const p2 = waypoints[i + 1] as { lng: number; lat: number };
  const p0 = i === 0
    ? { lng: 2 * p1.lng - p2.lng, lat: 2 * p1.lat - p2.lat }
    : (waypoints[i - 1] as { lng: number; lat: number });
  const p3 = i + 2 < waypoints.length
    ? (waypoints[i + 2] as { lng: number; lat: number })
    : { lng: 2 * p2.lng - p1.lng, lat: 2 * p2.lat - p1.lat };
  return {
    lng: catmullRom1D(p0.lng, p1.lng, p2.lng, p3.lng, u),
    lat: catmullRom1D(p0.lat, p1.lat, p2.lat, p3.lat, u),
  };
}

//
// Catmull-Rom cubic basis. p1 and p2 bracket the segment; p0 and
// p3 are the previous and next waypoints that define the tangents
// at p1 and p2. u is the local parameter in [0, 1].
//
function catmullRom1D(p0: number, p1: number, p2: number, p3: number, u: number): number {
  const u2 = u * u;
  const u3 = u2 * u;
  return 0.5 * (
    2 * p1 +
    (-p0 + p2) * u +
    (2 * p0 - 5 * p1 + 4 * p2 - p3) * u2 +
    (-p0 + 3 * p1 - 3 * p2 + p3) * u3
  );
}

//
// Find the index i such that times[i] <= t < times[i + 1]. Assumes
// times is sorted ascending and that t lies in [times[0], times[N-1]].
// Clamps at both ends so callers don't have to worry about the edge
// cases.
//
function findSegmentIndex(times: number[], t: number): number {
  if (t <= (times[0] as number)) return 0;
  const last = times.length - 1;
  if (t >= (times[last] as number)) return Math.max(0, last - 1);
  for (let i = 0; i < last; i++) {
    if (t >= (times[i] as number) && t < (times[i + 1] as number)) return i;
  }
  return Math.max(0, last - 1);
}

//
// Internal helpers.
//
function isPan(a: Action): a is PanAction {
  return a.type === 'pan';
}

function isRotate(a: Action): a is RotateAction {
  return a.type === 'rotate';
}

function byStart(a: Action, b: Action): number {
  return a.start - b.start;
}

function evalEasing(kind: Easing, t: number): number {
  return EASING[kind](Math.max(0, Math.min(1, t)));
}

//
// Convenience factory: build an Action with a fresh random id.
// Callers pass the body without id; we synthesize one.
//
// The DistributiveOmit is necessary because RotateAction itself is
// a union (absolute / delta variants). A plain `Omit<RotateAction,
// 'id'>` collapses the union down to the common fields, losing
// `deltaDegrees` and `target`. Distributing the Omit across union
// members preserves each variant's shape.
//
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export function createAction<A extends Action>(body: DistributiveOmit<A, 'id'>): A {
  return { ...body, id: crypto.randomUUID() } as unknown as A;
}

//
// Detect whether two actions on the same track would overlap in
// time. Used by the drag handler in Timeline.tsx to clamp positions
// so the engine's "no overlap per track" invariant holds.
//
export function overlaps(a: Action, b: Action): boolean {
  if (a.id === b.id) return false;
  if (trackOf(a) !== trackOf(b)) return false;
  return a.start < b.end && b.start < a.end;
}
